/**
 * CSS injection utilities for the search widget
 */

/**
 * Inject critical widget styles with !important into <style> tag
 */
export function injectWidgetStyles() {
  if (document.getElementById('nexus-search-widget-critical-styles')) return;

  const style = document.createElement('style');
  style.id = 'nexus-search-widget-critical-styles';
  style.textContent = `
    /* HIDE widget until styles are loaded - prevents flash of unstyled content */
    .nexus-search-widget[data-nexus-search]:not(.nexus-styles-ready) {
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    /* Show widget with smooth transition when ready */
    .nexus-search-widget[data-nexus-search].nexus-styles-ready {
      visibility: visible !important;
      opacity: 1 !important;
      pointer-events: auto !important;
      transition: opacity 0.15s ease-in !important;
    }

    /* Widget container layout */
    .nexus-search-widget[data-nexus-search] {
      display: flex !important;
      flex-wrap: nowrap !important;
      align-items: stretch !important;
      gap: 8px !important;
      background: var(--sw-container-bg, transparent) !important;
      padding: var(--sw-container-pad, 12px) !important;
      border-radius: var(--sw-container-r, 12px) !important;
      border: 1px solid var(--sw-container-bc, rgba(15,23,42,.2)) !important;
    }
    .nexus-search-widget[data-nexus-search] > div[data-type="input"] {
      flex: 2 1 0 !important;
      min-width: 0 !important;
    }
    .nexus-search-widget[data-nexus-search] > div[data-type="button"] {
      flex: 1 1 0 !important;
      min-width: 0 !important;
    }

    /* All pill-style buttons (location, guests, calendar) */
    .nexus-search-widget[data-nexus-search] button[data-button-type="pill"],
    .nexus-search-widget[data-nexus-search] div[data-button-type="pill"] > div {
      display: flex !important;
      align-items: center !important;
      gap: var(--sw-pill-gap, 10px) !important;
      border: 1px solid var(--sw-pill-bc, rgba(15,23,42,.2)) !important;
      border-radius: var(--sw-pill-r, 10px) !important;
      padding: var(--sw-pill-py, 10px) var(--sw-pill-px, 12px) !important;
      background: var(--sw-pill-bg, transparent) !important;
      font-family: var(--sw-pill-ff, inherit) !important;
      font-size: var(--sw-pill-fs, 14px) !important;
      font-weight: var(--sw-pill-fw, 600) !important;
      color: var(--sw-pill-fc, inherit) !important;
      min-width: var(--sw-pill-minw, 150px) !important;
      height: var(--sw-pill-h, 44px) !important;
      cursor: pointer !important;
      user-select: none !important;
      justify-content: space-between !important;
      width: 100% !important;
      box-sizing: border-box !important;
      transition: border-color 0.15s ease !important;
    }
    .nexus-search-widget[data-nexus-search] button[data-button-type="pill"]:hover,
    .nexus-search-widget[data-nexus-search] div[data-button-type="pill"] > div:hover {
      border-color: var(--sw-pill-bc-hover, #999999) !important;
    }

    /* Filter button */
    .nexus-search-widget[data-nexus-search] button[data-button-type="filter"] {
      display: inline-flex !important;
      align-items: center !important;
      gap: var(--sw-filter-gap, 8px) !important;
      border: 1px solid var(--sw-filter-bc, rgba(15,23,42,.2)) !important;
      border-radius: var(--sw-filter-r, 10px) !important;
      padding: var(--sw-filter-py, 10px) var(--sw-filter-px, 12px) !important;
      background: var(--sw-filter-bg, transparent) !important;
      font-family: var(--sw-filter-ff, inherit) !important;
      font-size: var(--sw-filter-fs, 14px) !important;
      font-weight: var(--sw-filter-fw, 600) !important;
      color: var(--sw-filter-fc, inherit) !important;
      min-width: var(--sw-filter-minw, 80px) !important;
      height: var(--sw-filter-h, 44px) !important;
      cursor: pointer !important;
      user-select: none !important;
      white-space: nowrap !important;
      width: 100% !important;
      box-sizing: border-box !important;
    }

    /* Search button */
    .nexus-search-widget[data-nexus-search] button[data-button-type="search"] {
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;
      border: 1px solid var(--sw-searchbtn-bc, transparent) !important;
      border-radius: var(--sw-searchbtn-r, 10px) !important;
      padding: var(--sw-searchbtn-py, 10px) var(--sw-searchbtn-px, 16px) !important;
      background: var(--sw-searchbtn-bg, #f38b4a) !important;
      font-family: var(--sw-searchbtn-ff, inherit) !important;
      font-size: var(--sw-searchbtn-fs, 14px) !important;
      font-weight: var(--sw-searchbtn-fw, 600) !important;
      color: var(--sw-searchbtn-fc, #ffffff) !important;
      min-width: var(--sw-searchbtn-minw, 90px) !important;
      height: var(--sw-searchbtn-h, 44px) !important;
      cursor: pointer !important;
      user-select: none !important;
      white-space: nowrap !important;
      width: 100% !important;
      box-sizing: border-box !important;
    }

    /* Mobile gap overrides */
    @media (max-width: 768px) {
      .nexus-search-widget[data-nexus-search] button[data-button-type="pill"],
      .nexus-search-widget[data-nexus-search] div[data-button-type="pill"] > div {
        gap: var(--sw-pill-gap-mobile, var(--sw-pill-gap, 6px)) !important;
      }
      .nexus-search-widget[data-nexus-search] button[data-button-type="filter"] {
        gap: var(--sw-filter-gap-mobile, var(--sw-filter-gap, 6px)) !important;
      }
    }
  `;
  document.head.appendChild(style);
}

/**
 * Inject responsive CSS for search widget (only once)
 */
export function ensureSearchWidgetResponsiveCSS() {
  if (document.getElementById("__search_widget_responsive_css")) return;
  const s = document.createElement("style");
  s.id = "__search_widget_responsive_css";
  s.textContent = `
/* Responsive layout for search widget */
@media (max-width: 1024px) {
  .nexus-search-widget[data-nexus-search] {
    flex-direction: column !important;
  }
  .nexus-search-widget[data-nexus-search] > div {
    flex: 1 1 auto !important;
    width: 100% !important;
  }
}

@media (max-width: 768px) {
  .nexus-search-widget[data-nexus-search] {
    flex-direction: column !important;
  }
  .nexus-search-widget[data-nexus-search] > div {
    flex: 1 1 auto !important;
    width: 100% !important;
  }
}
  `;
  document.head.appendChild(s);
}

/**
 * Inject the CSS that consumes calendar runtime vars (only once)
 */
export function ensureCalendarRuntimeCSS() {
  if (document.getElementById("__calendar_runtime_vars_css")) return;
  const s = document.createElement("style");
  s.id = "__calendar_runtime_vars_css";
  const SCOPE = `#nexus-calendar-section, .nexus-calendar, [data-nexus-calendar]`;
  s.textContent = `
/* Container & month surface */
${SCOPE} .nexus-custom-calendar,
${SCOPE} .nexus-calendar-month{
  background: var(--cal-ct-bg, #fff);
  border: 1px solid var(--cal-ct-bc, rgba(15,23,42,.2));
  padding: var(--cal-ct-pad-t,8px) var(--cal-ct-pad-r,8px) var(--cal-ct-pad-b,8px) var(--cal-ct-pad-l,8px);
}

/* Month heading */
${SCOPE} .nexus-calendar-month h4{
  font-size: var(--cal-month-fs,18px);
  font-weight: var(--cal-month-fw,800);
  font-family: var(--cal-month-ff,inherit);
  color: var(--cal-month-fc,#0f172a);
}

/* Weekdays */
${SCOPE} ul.weekdays li{
  background: var(--cal-wd-bg,#fff);
  color: var(--cal-wd-fc,#0f172a);
  border: 1px solid var(--cal-wd-bc,transparent);
  font-size: var(--cal-wd-fs,12px);
  font-weight: var(--cal-wd-fw,600);
  font-family: var(--cal-wd-ff,inherit);
  padding: var(--cal-wd-pad-t,6px) var(--cal-wd-pad-r,6px) var(--cal-wd-pad-b,6px) var(--cal-wd-pad-l,6px);
  text-decoration: var(--cal-wd-td, initial);
  text-decoration-color: var(--cal-wd-tdc, currentColor);
}

/* Day base */
${SCOPE} li.nexus-calendar-day{
  background: var(--cal-day-bg,#fff);
  color: var(--cal-day-fc,#0f172a);
  border: 1px solid var(--cal-day-bc,rgba(15,23,42,.12));
  font-size: var(--cal-day-fs,14px);
  font-weight: var(--cal-day-fw,700);
  font-family: var(--cal-day-ff,inherit);
  padding: var(--cal-day-pad-t,8px) var(--cal-day-pad-r,8px) var(--cal-day-pad-b,8px) var(--cal-day-pad-l,8px);
  text-decoration: var(--cal-day-td, initial);
  text-decoration-color: var(--cal-day-tdc, currentColor);
}

/* Day hover */
${SCOPE} li.nexus-calendar-day.uk-text-middle:hover{
  background: var(--cal-dayh-bg,#f3f4f6) !important;
  color: var(--cal-dayh-fc,#0f172a) !important;
  font-size: var(--cal-dayh-fs,14px);
  font-weight: var(--cal-dayh-fw,700);
  font-family: var(--cal-dayh-ff,inherit);
}

/* Past disabled */
${SCOPE} li.nexus-calendar-day.past-day.day-disabled{
  background: var(--cal-past-bg,#f9fafb);
  color: var(--cal-past-fc,#9ca3af);
}

/* Not available + Elementor selector parity (also checkout-date in their PHP) */
${SCOPE} .not-available,
${SCOPE} li.nexus-calendar-day.checkout-date{
  background: var(--cal-na-bg,#f3f4f6) !important;
  color: var(--cal-na-fc,#9ca3af) !important;
}

/* Selected range start / end / middle */
${SCOPE} li.nexus-calendar-day.uk-text-middle.nexus-from-day{
  background: var(--cal-from-bg,#f38b4a) !important;
  color: var(--cal-from-fc,#fff) !important;
  border-radius: var(--cal-from-r-t,8px) var(--cal-from-r-r,8px) var(--cal-from-r-b,8px) var(--cal-from-r-l,8px) !important;
}
${SCOPE} li.nexus-calendar-day.uk-text-middle.nexus-to-day{
  background: var(--cal-to-bg,#f38b4a) !important;
  color: var(--cal-to-fc,#fff) !important;
  border-radius: var(--cal-to-r-t,8px) var(--cal-to-r-r,8px) var(--cal-to-r-b,8px) var(--cal-to-r-l,8px) !important;
}
${SCOPE} li.nexus-calendar-day.uk-text-middle.nexus-in-between{
  background: var(--cal-mid-bg,#fde7d8) !important;
  border-radius: var(--cal-mid-r-t,8px) var(--cal-mid-r-r,8px) var(--cal-mid-r-b,8px) var(--cal-mid-r-l,8px) !important;
}

/* ::after badges for check-in/out */
${SCOPE} li.nexus-calendar-day.checkin-date::after{
  background: var(--cal-cia-bg,transparent);
  color: var(--cal-cia-fc,#0f172a);
  border: 1px solid var(--cal-cia-bc,transparent);
  padding: var(--cal-cia-pad-t,0) var(--cal-cia-pad-r,0) var(--cal-cia-pad-b,0) var(--cal-cia-pad-l,0);
  text-decoration: var(--cal-cia-td, initial);
  text-decoration-color: var(--cal-cia-tdc, currentColor);
}
${SCOPE} li.nexus-calendar-day.checkout-date::after{
  background: var(--cal-coa-bg,transparent);
  color: var(--cal-coa-fc,#0f172a);
  border: 1px solid var(--cal-coa-bc,transparent);
  padding: var(--cal-coa-pad-t,0) var(--cal-coa-pad-r,0) var(--cal-coa-pad-b,0) var(--cal-coa-pad-l,0);
  text-decoration: var(--cal-coa-td, initial);
  text-decoration-color: var(--cal-coa-tdc, currentColor);
}

/* Nav buttons (prev/next) + icon color */
${SCOPE} .listing-cal-next,
${SCOPE} .listing-cal-prev{
  border: 1px solid var(--cal-nav-bc,rgba(15,23,42,.12));
  width: var(--cal-nav-w,36px);
  height: var(--cal-nav-h,36px);
  border-radius: var(--cal-nav-r-t,10px) var(--cal-nav-r-r,10px) var(--cal-nav-r-b,10px) var(--cal-nav-r-l,10px);
  display: inline-grid;
  place-items: center;
}
${SCOPE} .listing-cal-next .uk-icon,
${SCOPE} .listing-cal-prev .uk-icon,
${SCOPE} .listing-cal-next [uk-icon],
${SCOPE} .listing-cal-prev [uk-icon]{
  color: var(--cal-nav-ic,#0f172a) !important;
  fill: var(--cal-nav-ic,#0f172a) !important;
}
${SCOPE} .listing-cal-next svg,
${SCOPE} .listing-cal-prev svg{
  fill: var(--cal-nav-ic,#0f172a) !important;
}

/* Calendar input field (trigger) - font styles only, NO border/background/padding */
${SCOPE} input[type="text"],
${SCOPE} .nexus-calendar-datepicker{
  font-size: var(--cal-input-fs,14px) !important;
  font-weight: var(--cal-input-fw,500) !important;
  font-family: var(--cal-input-ff,inherit) !important;
  color: var(--cal-input-fc,#0f172a) !important;
}
${SCOPE} input[type="text"]::placeholder,
${SCOPE} .nexus-calendar-datepicker::placeholder{
  color: var(--cal-input-placeholder-fc,#9ca3af) !important;
}

/* Dropdown container */
${SCOPE},
${SCOPE} .nexus-calendar-section-dropdown{
  background: var(--cal-dropdown-bg,#ffffff) !important;
  border-color: var(--cal-dropdown-bc,rgba(15,23,42,.2)) !important;
  border-radius: var(--cal-dropdown-r,12px) !important;
  box-shadow: var(--cal-dropdown-shadow,0 10px 25px rgba(0,0,0,.1)) !important;
  z-index: var(--cal-dropdown-z,9999) !important;
  max-width: var(--cal-dropdown-maxw,1200px) !important;
  padding: var(--cal-dropdown-pad-t,16px) var(--cal-dropdown-pad-r,16px) var(--cal-dropdown-pad-b,16px) var(--cal-dropdown-pad-l,16px) !important;
}
`;
  document.head.appendChild(s);
}

/**
 * Load Font Awesome Kit for icons - only if FA mode is being used
 */
export function ensureFontAwesome(needsFA = true) {
  // Don't load if not needed
  if (!needsFA) return;

  // Check if Font Awesome Kit is already loaded
  if (document.getElementById("__nexus_fontawesome_kit") ||
      document.querySelector('script[src*="kit.fontawesome.com"]') ||
      document.querySelector('link[href*="fontawesome"]')) {
    return;
  }

  const script = document.createElement("script");
  script.id = "__nexus_fontawesome_kit";
  script.src = "https://kit.fontawesome.com/94a7ddebae.js";
  script.crossOrigin = "anonymous";
  script.defer = true; // Defer loading to not block rendering
  document.head.appendChild(script);
}

/**
 * Inject search widget specific calendar input styles (loads AFTER calendar CSS)
 */
export function ensureSearchCalendarInputCSS() {
  if (document.getElementById("__search_calendar_input_css")) return;
  const s = document.createElement("style");
  s.id = "__search_calendar_input_css";
  s.textContent = `
/* Calendar/Dates button container in search widget - controlled by Input Fields > Calendar Button settings */
.nexus-calendar-input-container {
  background: var(--sw-cal-bg, transparent) !important;
  border-color: var(--sw-cal-bc, rgba(15,23,42,.2)) !important;
  border-style: solid !important;
  border-width: 1px !important;
  border-radius: var(--sw-cal-r, 10px) !important;
  padding: var(--sw-cal-py, 10px) var(--sw-cal-px, 12px) !important;
  min-width: var(--sw-cal-minw, 100px) !important;
  height: var(--sw-cal-h, 44px) !important;
  gap: var(--sw-pill-gap, 10px) !important;
  box-sizing: border-box !important;
}
/* Calendar input - font styles only, NO border/background/padding (higher specificity to override calendar CSS) */
.nexus-calendar-input-container input.nexus-calendar-datepicker[type="text"],
.nexus-calendar-input-container input.nexus-calendar-datepicker,
.nexus-calendar-input-container .nexus-calendar-datepicker,
[data-nexus-calendar] input.nexus-calendar-datepicker[type="text"],
[data-nexus-calendar] input.nexus-calendar-datepicker,
[data-nexus-calendar] .nexus-calendar-datepicker,
input.nexus-calendar-datepicker[type="text"],
input.nexus-calendar-datepicker,
.nexus-calendar-datepicker{
  font-size: var(--sw-pill-fs, 14px) !important;
  font-weight: var(--sw-pill-fw, 600) !important;
  font-family: var(--sw-pill-ff, inherit) !important;
  color: var(--sw-pill-fc, #0f172a) !important;
  border: none !important;
  border-width: 0 !important;
  border-style: none !important;
  background: transparent !important;
  background-color: transparent !important;
  padding: 0 !important;
  padding-top: 0 !important;
  padding-right: 0 !important;
  padding-bottom: 0 !important;
  padding-left: 0 !important;
  border-radius: 0 !important;
  min-width: 0 !important;
}
/* Force remove all borders - maximum specificity override */
.nexus-search-widget input.nexus-calendar-datepicker[type="text"],
.nexus-search-widget input.nexus-calendar-datepicker[type="text"]::placeholder,
.nexus-search-widget input.nexus-calendar-datepicker{
  border: 0 !important;
}
/* Force placeholder color - maximum specificity override */
input.nexus-calendar-datepicker::placeholder {
  color: var(--sw-cal-pc,#0f172a) !important;
  opacity: 1 !important;
}
/* Placeholder color */
[data-nexus-calendar] input.nexus-calendar-datepicker[type="text"]::placeholder,
[data-nexus-calendar] input.nexus-calendar-datepicker::placeholder,
[data-nexus-calendar] .nexus-calendar-datepicker::placeholder,
input.nexus-calendar-datepicker[type="text"]::placeholder,
input.nexus-calendar-datepicker::placeholder,
.nexus-calendar-datepicker::placeholder,
input.nexus-calendar-datepicker[type="text"]::-webkit-input-placeholder,
input.nexus-calendar-datepicker::-webkit-input-placeholder,
input.nexus-calendar-datepicker[type="text"]::-moz-placeholder,
input.nexus-calendar-datepicker::-moz-placeholder,
input.nexus-calendar-datepicker[type="text"]:-ms-input-placeholder,
input.nexus-calendar-datepicker:-ms-input-placeholder{
  color: var(--sw-cal-pc,#0f172a) !important;
  opacity: 1 !important;
}

/* Calendar dropdown default styles - protect against Elementor/theme overrides */
.nexus-calendar-section-dropdown {
  box-sizing: border-box !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
}

/* Calendar day buttons */
.nexus-calendar-day,
button.nexus-calendar-day {
  all: unset !important;
  box-sizing: border-box !important;
  aspect-ratio: 1 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 8px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
  background: #ffffff !important;
  color: #1a1a1a !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  padding: 8px !important;
  min-width: 36px !important;
  min-height: 36px !important;
}

.nexus-calendar-day:hover {
  background: #f3f4f6 !important;
  border-color: #d1d5db !important;
}

.nexus-calendar-day.past-day,
.nexus-calendar-day.day-disabled {
  opacity: 0.4 !important;
  cursor: not-allowed !important;
  background: #f9fafb !important;
}

.nexus-calendar-day.checkin-date,
.nexus-calendar-day.checkout-date {
  background: #111827 !important;
  color: #ffffff !important;
  border-color: #111827 !important;
}

.nexus-calendar-day.in-range {
  background: #f3f4f6 !important;
  border-color: #e5e7eb !important;
}

/* Flexibility buttons */
[data-flex-btn],
button[data-flex-btn] {
  all: unset !important;
  box-sizing: border-box !important;
  padding: 8px 16px !important;
  border-radius: 24px !important;
  border: 1px solid #d1d5db !important;
  background: white !important;
  color: #374151 !important;
  font-size: 13px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
  font-weight: 500 !important;
  display: inline-block !important;
  text-align: center !important;
  line-height: 1.5 !important;
}

[data-flex-btn].active,
button[data-flex-btn].active {
  border-color: #111827 !important;
  background: #111827 !important;
  color: white !important;
}

[data-flex-btn]:hover,
button[data-flex-btn]:hover {
  background: #f9fafb !important;
  border-color: #9ca3af !important;
}

[data-flex-btn].active:hover,
button[data-flex-btn].active:hover {
  background: #1f2937 !important;
  border-color: #111827 !important;
}

/* Calendar month headers */
.calendar-header h4 {
  margin: 0 !important;
  padding: 0 !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #1a1a1a !important;
}

/* Weekday headers */
.weekdays {
  list-style: none !important;
  padding: 0 !important;
  margin: 0 0 8px 0 !important;
}

.weekdays li {
  text-align: center !important;
  padding: 8px 4px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  color: #6b7280 !important;
  text-transform: uppercase !important;
}
`;
  document.head.appendChild(s);
}
